import { FieldInputProps, FormikProps } from 'formik';
import { ReactElement } from 'react';
import { OutreachTypes } from '../../urls';

import i18n from '../../utils/i18n';
import { ClauseType, LocationAttributes } from './components/AddMonitor/types';
import { MonitorCriteriaEnum } from './components/MonitorInfo/fixtures';
import { FormikSupportOutreachTriggerValue } from './components/OutreachConfigForm/controls/OutreachConfig/support/types';
import {
  OutreachSubscriberPushDtoType,
  OutreachSupportEmailDtoType,
  OutreachSupportWebhookDtoType,
} from './components/OutreachConfigForm/dtos/outreach-support-email.dto';

export type PhraseType = {
  logic: LogicOperator;
  clauseIds: string[];
  groups: PhraseType[];
  key: string;
  parent?: string;
  seq?: string[] | null;
  sub?: string;
};

export enum OutreachEnum {
  unstarted,
  active,
  ended,
  disabled,
  unconfig,
}

export type CriteriaSingleton = {
  operator: string;
  criteriaType: keyof typeof MonitorCriteriaEnum;
  selectedValue: string | number;
};

type Outreach = {
  label: string;
  color: string;
};

export const getOutreachType = (type: OutreachEnum): Outreach => {
  return {
    [OutreachEnum.unstarted]: {
      label: i18n.t('monitors.outreachStatuses.unstarted'),
      color: '#FFC500',
    },
    [OutreachEnum.active]: {
      label: i18n.t('monitors.outreachStatuses.active'),
      color: '#17E3AE',
    },
    [OutreachEnum.ended]: {
      label: i18n.t('monitors.outreachStatuses.ended'),
      color: '#0FDCFF',
    },
    [OutreachEnum.disabled]: {
      label: i18n.t('monitors.outreachStatuses.disabled'),
      color: '#FA1478',
    },
    [OutreachEnum.unconfig]: {
      label: i18n.t('monitors.outreachStatuses.unconfigured'),
      color: '#c6c6c6',
    },
  }[type] as Outreach;
};

export const getHarvestSegmentType = () => ({
  enabled: {
    label: i18n.t(
      'monitors.monitorInfo.monitorInfoMetrics.harvestSegment.syncOn',
    ),
    color: '#17E3AE',
  },
  disabled: {
    label: i18n.t(
      'monitors.monitorInfo.monitorInfoMetrics.harvestSegment.syncOff',
    ),
    color: '#c6c6c6',
  },
});

export type CallBackFunction = (id?: string) => void;

export type MonitorCellProps = {
  name: string;
  id: string;
  subscriberOutreach?: {
    id?: string;
    status: keyof typeof OutreachEnum;
  };
  supportOutreach?: {
    id?: string;
    status: keyof typeof OutreachEnum;
  };
  supportWebhook?: {
    id?: string;
    status: keyof typeof OutreachEnum;
  };
  busy?: boolean;
  affectedRange?: string;
  menuHandler?: { [key: string]: CallBackFunction };
  frequencyInHours?: number;
  displayOutreachInsights?: boolean;
};

export type MonitorCellStyles = {
  root: string;
};

export type Criterias = {
  [key: string]: ClauseType;
};

export type CriteriaSet = {
  sequence: string;
  criterias: Criterias;
};
export type MonitorPositionInfo = { order?: number };

export type LogicOperator = 'AND' | 'OR';

// The Monitor type is also used for Monitor creation so some of the fields may not be available e.g. id
// @TODO the monitor creation returns a monitorId but monitors query returns id - previously the types were different see MonitorListItem

export type Monitor = {
  id?: string;
  monitorId?: string; // see above TODO
  name: string;
  criteria?: CriteriaSet;
  harvestSyncEnabled?: boolean;
  monitorPositionInfo?: MonitorPositionInfo;
  frequencyInHours: number | null;
  issueRelatedWorkflows: {
    workflowId: string;
    workflowName: string;
    enabled: boolean;
  }[];
  outreach?: {
    subscriberPush?: {
      id?: string;
      status: keyof typeof OutreachEnum;
    };
    supportEmail?: {
      id?: string;
      status: keyof typeof OutreachEnum;
    };
    supportWebhook?: {
      id?: string;
      status: keyof typeof OutreachEnum;
    };
  };
  locationAttributes?: LocationAttributes;
  logicalExpression?: string;
};

export type MonitorDetails = {
  monitorName: string;
  monitorId: string;
}[];

export type GridItemType = {
  metric: string | JSX.Element;
  value: string | number | JSX.Element;
};

export type ValueDataTypes =
  | 'integer'
  | 'object'
  | 'string'
  | 'float'
  | 'boolean';
export type MetadataAllowedValue = {
  display: string;
  key: string;
};

export type SpecificValueMetadata = {
  selectedValueDatatype: ValueDataTypes;
  allowedValues?: MetadataAllowedValue[];
} & Partial<MetadataTimeSchedule>;
export type MetadataAllowedInputs<T extends string> = Record<
  T,
  SpecificValueMetadata
>;
export type MetadataGroup<F extends string> = {
  allowedInputs: MetadataAllowedInputs<F>;
  selectedValueDatatype: ValueDataTypes;
  displayName?: string;
};
export type MetadataTimeSchedule = MetadataGroup<
  'recurrence' | 'recurrenceUnit' | 'weekdays'
>;
export type MetadataTime = MetadataGroup<'schedule'> & {
  schedule: MetadataTimeSchedule;
};
export type OutreachMetadataPushResponse = {
  maxFrequency: MetadataGroup<'maxAllowed' | 'recurrence' | 'recurrenceUnit'>;
  time: MetadataTime;
};
export type OutreachMetadataEmailResponse = OutreachMetadataPushResponse & {
  maxIncreaseRateOverADay: MetadataGroup<
    'issueClearingLocationPercentage' | 'issueTriggeringLocationPercentage'
  >;
  maxPercentage: MetadataGroup<
    'issueClearingLocationPercentage' | 'issueTriggeringLocationPercentage'
  >;
  maxValue: MetadataGroup<
    'issueClearingLocationCount' | 'issueTriggeringLocationCount'
  >;
};
export type OutreachMetadataResponse = OutreachMetadataEmailResponse;
export type OutreachMetadata = Record<OutreachTypes, OutreachMetadataResponse>;

export type MonitorData = {
  ts: string;
  timeLabel: string;
  location_count: number;
};

export type MonitorHourlyFreqData = {
  average_health_score: number;
  location_count: number;
  new_location_count_7d: number | null;
  ts: string;
};

export type MonitorOutreachHourlyStat = {
  ts: string;
  support_start_count: number;
  support_clear_count: number;
  support_block_count: number;
  subscriber_open_count: number;
  subscriber_receive_count: number;
  subscriber_send_count: number;
  subscriber_block_count: number;
  subscriber_bounce_count: number;
  subscriber_error_count: number;
  webhook_count: number;
  webhook_error_count: number;
};

export enum OutreachConfigFormFields {
  SupportOutreach = 'supportOutreach',
  SupportOutreachEnabled = 'supportOutreachEnabled',
  SubscriberOutreach = 'subscriberOutreach',
  SubscriberOutreachEnabled = 'subscriberOutreachEnabled',
  WebhookOutreach = 'webhookOutreach',
  WebhookOutreachEnabled = 'webhookOutreachEnabled',
  SelectedWebhook = 'selectedWebhook',
  SelectedWebhookName = 'selectedWebhookName',
}

export enum SubscriberContentFormFields {
  OutreachRuleName = 'outreachRuleName',
  Subject = 'subject',
  Message = 'message',
  UrlEnabled = 'urlEnabled',
  UrlLinkReference = 'urlLinkReference',
  UrlText = 'urlText',
  UrlLink = 'urlLink',
}

export enum SubscriberClearMessageFormFields {
  Subject = 'subject',
  Message = 'message',
}

export enum OutreachTimeFormFields {
  From = 'from',
  To = 'to',
}
export enum SupportOutreachTriggerFormFields {
  IsOneTimeOnly = 'isOneTimeOnly',
  TriggerType = 'triggerType',
  TimeRepeatEvery = 'timeRepeatEvery',
  TimeRepeatEveryUnit = 'timeRepeatEveryUnit',
  TimeRepeatWeekdays = 'timeRepeatWeekdays',
  ScheduleMaxFrequencyOfSupportOutreach = 'scheduleMaxFrequencyOfSupportOutreach',
  ScheduleMaxFrequencyOfSupportOutreachEvery = 'scheduleMaxFrequencyOfSupportOutreachEvery',
  ScheduleMaxFrequencyOfSupportOutreachUnit = 'scheduleMaxFrequencyOfSupportOutreachUnit',
  SendClearEmailWhenIssueCleared = 'sendClearEmailWhenIssueCleared',
  IssueTriggeredWhen = 'issueTriggeredWhen',
  IssueClearedWhen = 'issueClearedWhen',
}

export enum SubscriberOutreachTriggerFormFields {
  IsOneTimeOnly = 'isOneTimeOnly',
  TriggerType = 'triggerType',
  TimeRepeatEvery = 'timeRepeatEvery',
  TimeRepeatEveryUnit = 'timeRepeatEveryUnit',
  TimeRepeatWeekdays = 'timeRepeatWeekdays',
  ScheduleMaxFrequencyOfSupportOutreach = 'scheduleMaxFrequencyOfSupportOutreach',
  ScheduleMaxFrequencyOfSupportOutreachEvery = 'scheduleMaxFrequencyOfSupportOutreachEvery',
  ScheduleMaxFrequencyOfSupportOutreachUnit = 'scheduleMaxFrequencyOfSupportOutreachUnit',
  SendClearEmailWhenIssueCleared = 'sendClearEmailWhenIssueCleared',
  IssueTriggeredWhen = 'issueTriggeredWhen',
  IssueClearedWhen = 'issueClearedWhen',
}

export enum SubscriberOutreachContentFormFields {
  OutreachRuleName = 'outreachRuleName',
  OutreachMethod = 'outreachMethod',
  Subject = 'subject',
  Message = 'message',
  UrlEnabled = 'urlEnabled',
  LinkReference = 'urlLinkReference',
  UrlText = 'urlText',
  UrlLink = 'urlLink',
}

export enum SupportOutreachContentFormFields {
  OutreachRuleName = 'outreachRuleName',
  OutreachMethod = 'outreachMethod',
  Recipients = 'recipients',
  Subject = 'subject',
  Introduction = 'introduction',
}

export enum SupportOutreachClearMessageFormFields {
  Recipients = 'recipients',
  Subject = 'subject',
  Introduction = 'introduction',
}

export type OutreachConfigGenericFormField<T> = {
  name: string;
  field: FieldInputProps<T>;
  form: FormikProps<T>;
};

export enum OutreachMethods {
  Push = 'push',
  Email = 'email',
}

export type TimeDropdownProperties =
  | OutreachTimeFormFields.From
  | OutreachTimeFormFields.To;
export type SupportTriggerRendererProps<
  T extends FormikSupportOutreachTriggerValue,
  D
> = {
  renderDropdownFor: (property: D) => ReactElement;
  triggerType: OutreachTriggerTypes;
  outreachType: OutreachTypes;
} & OutreachConfigGenericFormField<T>;

export enum OutreachTriggerTypes {
  Time = 'time',
  MaxValue = 'maxValue',
  MaxPercentage = 'maxPercentage',
  MaxIncreaseRate = 'maxIncreaseRateOverADay',
  CSPOutageDetected = 'cspOutage',
}

// @TODO: To replace it when we discover the types
export type SaveOutreachConfigDto = any;
export type UpdateOutreachConfigDto = any;
export type SaveOutreachWebhookConfig = {
  ruleName: string;
  enabled: boolean;
  startTime: any;
  endTime: any;
  triggerConfig: any;
  notifyIssueClearMessage: boolean;
  issueMessageConfig: {
    webhooks: string[];
  };
};

export type OutreachConfigBaseResponse = {
  createdAt: string;
  updatedAt: string;
  outreachId: string;
  monitorId: string;
  partnerId: string;
  triggerType: OutreachTriggerTypes;
  triggerRelatedInfo: any;
};

export type OutreachConfigResponse = OutreachConfigBaseResponse &
  OutreachSupportEmailDtoType;
export type OutreachConfigResponseWebhook = OutreachConfigBaseResponse &
  OutreachSupportWebhookDtoType;
export type OutreachSubscriberConfigResponse = OutreachConfigBaseResponse &
  OutreachSubscriberPushDtoType;

export type ConfigurationEmailPayload = {
  name: string;
  email: string;
  language: string;
};

export type ConfigurationEmailResponse = ConfigurationEmailPayload & {
  id: string;
  monitorDetails: MonitorDetails;
  autoMonitorDetails?: MonitorDetails;
};

export type ConfigurationCTAUrlPayload = {
  urlLink: string;
};

export type ConfigurationCTAUrlResponse = ConfigurationCTAUrlPayload & {
  id: string;
  monitorDetails: MonitorDetails;
};

export type FormikMessageField = {
  recipients: string[];
  subject: string;
  introduction: string;
};

export type FormikSubscriberOutreachClearValue = {
  subject: string;
  message: string;
};

export type OutreachStatCounts = OutreachSupportCounts &
  OutreachSubscriberCounts &
  OutreachWebhookCounts;

export type OutreachSupportCounts = {
  emailsSent: number;
  ts: string;
};

export type OutreachWebhookCounts = {
  webhookCount: number;
  ts: string;
};

export type OutreachSubscriberCounts = {
  pushUnreadCount: number;
  pushReadCount: number;
  sendCount?: number;
  receivedCount?: number;
  clickedCount?: number;
  blockedCount?: number;
  errorCount?: number;
  ts: string;
};

type SendTestMessageConfigName = {
  issue: 'issueMessageConfig';
  clear: 'clearMessageConfig';
};
export type MessageTypes = 'issue' | 'clear';
export type OutreachSendTestSupportMessagePayload<T extends MessageTypes> = {
  outreachId?: string;
} & Omit<
  OutreachSupportEmailDtoType,
  'issueMessageConfig' | 'clearMessageConfig'
> &
  Record<
    SendTestMessageConfigName[T],
    {
      recipients: string[];
      subject: string;
      introduction: string;
    }
  >;

export enum OutreachConfigSections {
  Config = 'outreachConfig',
  SupportOutreachTime = 'supportOutreachTime',
  SupportOutreachTimeWebhook = 'supportOutreachTimeWebhook',
  SupportOutreachTrigger = 'supportOutreachTrigger',
  SupportOutreachTriggerWebhook = 'supportOutreachTriggerWebhook',
  SupportOutreachContent = 'supportOutreachContent',
  SupportOutreachClearMessage = 'supportOutreachClearMessage',
  SubscriberOutreachTime = 'subscriberOutreachTime',
  SubscriberOutreachTrigger = 'subscriberOutreachTrigger',
  SubscriberOutreachContent = 'subscriberOutreachContent',
  SubscriberOutreachClearMessage = 'subscriberOutreachClearMessage',
}

export type TriggerTypeRendererGenericProps = SupportTriggerRendererProps<
  FormikSupportOutreachTriggerValue,
  any
>;

export type OutreachInsightsKey = 'on' | 'off';

export type OutreachInsightsTogglerItem = {
  key: OutreachInsightsKey;
  label: string;
};

export type GeoInfo = {
  lat: number;
  long: number;
};

export type IssueMapMarker = {
  geoInfo: GeoInfo;
  locationId: string;
};

export type LocationFilterType = {
  columnName: string;
  columnValue: string;
  operator: string;
};

export interface FilterData {
  categories: object;
  filter: object[];
  logicalExpression: string;
  rowCount: number;
  segmentId: string;
  segmentName: string;
  sqlExpression: string;
}

// Query types

export interface UnresolvedLocationListItem {
  customer_id: string;
  location_id: string;
  account_id: string;
  health_score: number;
  ts: string;
}

export enum MonitorsTab {
  User = 'user',
  Autonomous = 'autonomous',
  AccessNetwork = 'access-network',
}
// Autonomous Monitors

export enum AutonomousMonitorType {
  DownloadLow = 'download_low',
  DownloadVolatile = 'download_volatile',
  PodPlacement = 'pod_placement',
  DeviceQoe = 'device_qoe',
  FailedOptimizations = 'failed_optimizations',
  ParallelNetwork = 'parallel_network',
  TooManyOptimizations = 'too_many_optimizations',
  CspOutage = 'csp_outage',
  OfflineNetwork = 'offline_network',
  EvilTwin = 'evil_twin',
  OfflinePod = 'offline_pod',
  PodQoe = 'pod_qoe',
}
export interface AutonomousMonitor {
  id: string;
  name: string;
  frequencyInHours: number;
  monitorType: AutonomousMonitorType;
  green: {
    range: {
      durationInMinsLowerBound: number;
      durationInMinsUpperBound: number;
    };
  };
  yellow: {
    range: {
      durationInMinsLowerBound: number;
      durationInMinsUpperBound: number;
    };
    threshold: number;
    typical: number;
  };
  red: {
    range: {
      durationInMinsLowerBound: number;
      durationInMinsUpperBound: number;
    };
    threshold: number;
    typical: number;
  };
}

export interface AutonomousMonitorDetailed extends AutonomousMonitor {
  outreach: {
    supportEmail?: {
      id: string;
      status: keyof typeof OutreachEnum;
    };
    subscriberPush?: {
      id: string;
      status: keyof typeof OutreachEnum;
    };
    supportWebhook?: {
      id: string;
      status: keyof typeof OutreachEnum;
    };
  };
}

export interface AutonomousMonitorLocation {
  location_id: string;
  account_id: string;
  health_score: number;
  ts: string;
  latitude: number;
  longitude: number;
}

export interface AutonomousMonitorEmailOutreach {
  outreachId: string;
  enabled: boolean;
  recipients: string[];
}
